import React, { useEffect } from "react";
import { useGlobalNotifier, useMessage } from "@vrf/fr-vrf-core-react";
import {
  FrVrfActionList,
  FrVrfAlertBar,
  FrVrfButton,
  FrVrfDropdownField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfMultipleLineInputField,
  FrVrfSingleLineDisplayField,
  FrVrfTextDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { createModal } from "../../modal";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import {
  useGetRoleCodeList,
  useGetTeamCodeList,
  useGetUsers,
} from "../../../services/data-list";
import {
  RoleCvrgType,
  RoleType,
} from "../../../util/general-code/enums/general-codes";
import {
  UserSearchDialogContent,
  UserSearchDialogInitialState,
  useUserSearchDialogState,
} from "../user-search-dialog";
import {
  useInputStakeholderDialogState,
  InputStakeholderDialogInitialState,
  FieldPath,
} from ".";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import { DisplayFilterType } from "../project-stakeholder-list";

const MANUAL_LINK_LIST = [
  {
    label: "1. Create Project",
    value:
      "https://confluence.fastretailing.com/pages/viewpage.action?pageId=793717328",
  },
  {
    label: "2. Assign Requirements Definer",
    value:
      "https://confluence.fastretailing.com/pages/viewpage.action?pageId=847035100",
  },
  {
    label: "8. Assign Stakeholders ~ Start Project",
    value:
      "https://confluence.fastretailing.com/pages/viewpage.action?pageId=817541429",
  },
];

export const InputStakeholderDialogContent: React.FC = () => {
  const { showConfirmationDialog, resetUserError } = useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const [getRoleCodeListResponse, getRoleCodeListRequest] =
    useGetRoleCodeList();
  const [getTeamCodeListResponse, getTeamCodeListRequest] =
    useGetTeamCodeList();
  const [getUsersResponse, getUsersRequest] = useGetUsers();
  const [userSearchDialogState, setUserSearchDialogState] =
    useUserSearchDialogState();
  const [pageState, setPageState] = useInputStakeholderDialogState();
  const { moveToModalPage, closeModalPage } = usePageNavigation();

  const fixedTeamCds = new Map<string, string>([
    [RoleType.DIP_MANAGER, RoleCvrgType.JEDI],
    [RoleType.PROJECT_OWNER, RoleCvrgType.PROJECT],
    [RoleType.PIC_OF_IF_RD, RoleCvrgType.PROJECT],
    [RoleType.PROJECT_APPROVER, RoleCvrgType.PROJECT],
  ]);

  const closeModal = (isUpdated: boolean) => {
    if (!isUpdated) {
      resetUserError();
      setPageState(InputStakeholderDialogInitialState);
      return;
    }
    closeModalPage();

    setPageState((prevState) => ({
      ...prevState,
      isShow: false,
      status: {
        ...prevState.status,
        isUpdated: true,
      },
    }));
  };

  const onInitialDisplayed = (): void => {
    moveToModalPage();
    resetUserError();
    getRoleCodeListRequest({});
    getTeamCodeListRequest({});
  };
  useEffect(onInitialDisplayed, []);

  const searchSelectedUser = () => {
    if (!pageState.saved.userId) return;
    getUsersRequest({ userId: pageState.saved.userId });
  };
  useEffect(searchSelectedUser, [pageState.saved.userId]);

  const onGetRoleCodeListResponse = () => {
    if (getRoleCodeListResponse === undefined) return;

    const getFilteredRoleCodeList = (filter: String) => {
      if (filter === DisplayFilterType.Dip) {
        return getRoleCodeListResponse.roleCodeList.filter((e) =>
          [
            RoleType.DIP_MANAGER,
            RoleType.DIP_APPROVER,
            RoleType.PIC_OF_DIP_APP,
            RoleType.PIC_OF_DIP_INFRA,
            RoleType.PIC_OF_IF_RD,
            RoleType.PROJECT_OWNER,
            RoleType.PROJECT_APPROVER,
            RoleType.PIC_OF_COMPONENT_APP,
            RoleType.PIC_OF_COMPONENT_INFRA,
          ].includes(e.value as RoleType),
        );
      }
      if (filter === DisplayFilterType.Project) {
        return getRoleCodeListResponse.roleCodeList.filter((e) =>
          [
            RoleType.PROJECT_OWNER,
            RoleType.PROJECT_APPROVER,
            RoleType.PIC_OF_COMPONENT_APP,
            RoleType.PIC_OF_COMPONENT_INFRA,
          ].includes(e.value as RoleType),
        );
      }
      return getRoleCodeListResponse.roleCodeList.filter(
        (e) => ![RoleType.GOD, RoleType.SYSTEM].includes(e.value as RoleType),
      );
    };

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        roleCdList: getFilteredRoleCodeList(prevState.displayFilter).map(
          (e) => ({
            label: e.label,
            value: e.value,
            cvrgType: e.cvrgType,
          }),
        ),
      },
    }));
  };
  useEffect(onGetRoleCodeListResponse, [getRoleCodeListResponse]);

  const onGetTeamCodeListResponse = () => {
    if (getTeamCodeListResponse === undefined) return;

    // Exclude Hub flagged components from the list of component.
    const dipList = getTeamCodeListResponse.teamCodeList
      .filter((e) => e.cvrgType === RoleCvrgType.DIP)
      .map((e) => e.value);
    const filteredTeamCodeList = getTeamCodeListResponse.teamCodeList.filter(
      (e) =>
        e.cvrgType !== RoleCvrgType.ROOT_COMPONENT ||
        ![...dipList].includes(e.value),
    );

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        teamCdList: filteredTeamCodeList.map((e) => ({
          label: e.label,
          value: e.value,
          cvrgType: e.cvrgType,
        })),
      },
    }));
  };
  useEffect(onGetTeamCodeListResponse, [getTeamCodeListResponse]);

  const onGetUsersResponse = () => {
    if (getUsersResponse === undefined) return;

    if (getUsersResponse.userList.length === 0) {
      setPageState((prevState) => ({
        ...prevState,
        status: {
          ...prevState.status,
          notFound: true,
        },
      }));
      return;
    }

    setPageState((prevState) => ({
      ...prevState,
      edited: {
        ...prevState.edited,
        userRoleList: getUsersResponse.userList[0].userRoleList,
        userName: getUsersResponse.userList[0].userName,
        mlAdrs: getUsersResponse.userList[0].mlAdrs,
      },
      saved: {
        ...prevState.edited,
        userRoleList: getUsersResponse.userList[0].userRoleList,
        userName: getUsersResponse.userList[0].userName,
        mlAdrs: getUsersResponse.userList[0].mlAdrs,
      },
    }));
  };
  useEffect(onGetUsersResponse, [getUsersResponse]);

  /**
   * User Search Dialog.
   */
  const showUserSearchDialog = () => {
    setUserSearchDialogState({
      ...userSearchDialogState,
      userName:
        pageState.status.searchType === "UserName"
          ? pageState.status.searchStr
          : "",
      userId:
        pageState.status.searchType === "UserId"
          ? pageState.status.searchStr
          : "",
      isShow: true,
    });
  };

  const onListenSelectUser = (): void => {
    if (!userSearchDialogState) return;
    const res = { ...userSearchDialogState };

    if (res.isShow === true) return;
    if (!res.selectedUserId || !res.selectedUserName || !res.selectedUsermlAdrs)
      return;

    if (res.isApply) {
      setPageState((prevState) => ({
        ...prevState,
        edited: {
          ...prevState.edited,
          userId: res.selectedUserId ?? "",
          userName: res.selectedUserName ?? "",
          mlAdrs: res.selectedUsermlAdrs ?? "",
          userRoleList: res.selectedUserRoleList,
        },
        status: {
          ...prevState.status,
          notFound: false,
        },
      }));
    }

    setUserSearchDialogState({
      ...UserSearchDialogInitialState,
      selectedUserId: "",
      selectedUserName: "",
      selectedUsermlAdrs: "",
      selectedUserRoleList: [],
    });
  };
  useEffect(onListenSelectUser, [userSearchDialogState.isShow]);

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;

    switch (name) {
      case "searchStr":
      case "searchType":
        setPageState((prevState) => ({
          ...prevState,
          status: {
            ...prevState.status,
            [name]: value,
            invalidList: isValid
              ? prevState.status.invalidList.filter((e) => e !== name)
              : prevState.status.invalidList.concat(name),
          },
        }));
        break;
      case "roleCd":
        setPageState((prevState) => ({
          ...prevState,
          edited: {
            ...prevState.edited,
            roleCd: value,
            roleName:
              prevState.codes.roleCdList?.find((e) => e.value === value)
                ?.label ?? "",
            teamCd: fixedTeamCds.has(value)
              ? (fixedTeamCds.get(value) ?? "")
              : prevState.codes.teamCdList?.find(
                    (e) => e.value === prevState.edited.teamCd,
                  )?.cvrgType ===
                  prevState.codes.roleCdList?.find((e) => e.value === value)
                    ?.cvrgType
                ? prevState.edited.teamCd
                : "",
          },
          status: {
            ...prevState.status,
            invalidList: isValid
              ? prevState.status.invalidList.filter((e) => e !== name)
              : prevState.status.invalidList.concat(name),
          },
        }));
        break;
      case "teamCd":
        setPageState((prevState) => ({
          ...prevState,
          edited: {
            ...prevState.edited,
            roleCd:
              prevState.codes.roleCdList?.find(
                (e) => e.value === prevState.edited.roleCd,
              )?.cvrgType ===
              prevState.codes.teamCdList?.find((e) => e.value === value)
                ?.cvrgType
                ? prevState.edited.roleCd
                : "",
            teamCd: value,
          },
          status: {
            ...prevState.status,
            invalidList: isValid
              ? prevState.status.invalidList.filter((e) => e !== name)
              : prevState.status.invalidList.concat(name),
          },
        }));
        break;
      default:
        setPageState((prevState) => ({
          ...prevState,
          edited: {
            ...prevState.edited,
            [name]: value,
          },
          status: {
            ...prevState.status,
            invalidList: isValid
              ? prevState.status.invalidList.filter((e) => e !== name)
              : prevState.status.invalidList.concat(name),
          },
        }));
        break;
    }
  };

  const onClickCancel = () => {
    if (JSON.stringify(pageState.saved) === JSON.stringify(pageState.edited)) {
      closeModal(false);
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: () => {
          closeModal(false);
        },
      });
    }
  };

  const onClickUpdate = () => {
    if (!validate()) return;
    if (pageState.edited.roleName === "") {
      closeModalPage();
      setPageState((prevState) => ({
        ...prevState,
        isShow: false,
        edited: {
          ...prevState.edited,
          roleName:
            prevState.codes.roleCdList?.find(
              (e) => e.value === prevState.edited.roleCd,
            )?.label ?? "",
        },
        status: {
          ...prevState.status,
          isUpdated: true,
        },
      }));
      return;
    }

    closeModal(true);
  };

  const validate = () => {
    let isValid = true;
    const errorMessages: FieldPath = {
      ...InputStakeholderDialogInitialState.fieldPath,
    };

    // Mandatory Check
    if (pageState.edited.userId === "") {
      errorMessages.searchStr = messageForScreen(
        ApplicationMessageScreen.APP_SCN_00080,
      );
      isValid = false;
    }
    if (pageState.edited.teamCd === "") {
      errorMessages.teamCd = messageForScreen(
        ApplicationMessageScreen.APP_SCN_00001,
      );
      isValid = false;
    }
    if (pageState.edited.roleCd === "") {
      errorMessages.roleCd = messageForScreen(
        ApplicationMessageScreen.APP_SCN_00001,
      );
      isValid = false;
    }
    if (!isValid) {
      setPageState((prevState) => ({ ...prevState, fieldPath: errorMessages }));
      return isValid;
    }

    // Correlation check
    const cvrgType = pageState.codes.roleCdList?.find(
      (e) => e.value === pageState.edited.roleCd,
    )?.cvrgType;

    switch (cvrgType) {
      case RoleCvrgType.ROOT_COMPONENT:
        return true;
      case RoleCvrgType.JEDI:
        if (
          pageState.edited.userRoleList?.some(
            (e) => e.roleCd === pageState.edited.roleCd,
          )
        ) {
          return true;
        }
        break;
      case RoleCvrgType.DIP:
        if (
          pageState.edited.userRoleList?.some(
            (e) => e.roleCd === pageState.edited.roleCd,
          ) &&
          pageState.edited.userRoleList?.some(
            (e) => e.cvrgCd === pageState.edited.teamCd,
          )
        ) {
          return true;
        }
        break;
      case RoleCvrgType.PROJECT:
        if (
          pageState.edited.roleCd !== RoleType.PIC_OF_IF_RD ||
          pageState.edited.userRoleList?.some((e) =>
            [RoleType.DIP_MANAGER, RoleType.DIP_APPROVER].includes(
              e.roleCd as RoleType,
            ),
          )
        ) {
          return true;
        }
    }

    errorMessages.searchStr = messageForScreen(
      ApplicationMessageScreen.APP_SCN_00078,
    );
    setPageState((prevState) => ({ ...prevState, fieldPath: errorMessages }));
    return false;
  };

  const getFilteredTeamsCdList = (roleCd: string) => {
    if (fixedTeamCds.has(roleCd)) {
      return pageState.codes.teamCdList;
    }

    if (
      [
        RoleType.DIP_APPROVER,
        RoleType.PIC_OF_DIP_APP,
        RoleType.PIC_OF_DIP_INFRA,
      ].includes(roleCd as RoleType)
    ) {
      return pageState.codes.teamCdList?.filter(
        (e) => e.cvrgType === RoleCvrgType.DIP,
      );
    }

    return pageState.codes.teamCdList
      ?.filter((e) => e.cvrgType === RoleCvrgType.ROOT_COMPONENT)
      .map((e) => ({
        label: e.value,
        value: e.value,
        cvrgType: e.cvrgType,
      }));
  };

  const getContent = () => {
    return (
      <>
        {userSearchDialogState.isShow && (
          <>
            <UserSearchDialogContent />
          </>
        )}
        {pageState.status.notFound && (
          <FrVrfAlertBar
            messageList={[
              {
                message: messageForScreen(
                  ApplicationMessageScreen.APP_SCN_00125,
                  "Specified user",
                ),
              },
            ]}
          />
        )}
        <div
          style={{
            paddingTop: "10px",
            paddingBottom: "30px",
            marginLeft: "30px",
          }}
        >
          <FrVrfFormPanel columns={1}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <FrVrfButton
                  content="Search User"
                  size="medium"
                  theme="primary"
                  onClicked={showUserSearchDialog}
                />
                <FrVrfTextDisplayField
                  style={{
                    "--vrf-text-display-field-font-size": "16px",
                    "--vrf-font-color": "red",
                    marginLeft: "10px",
                  }}
                  text={pageState.fieldPath.searchStr}
                />
              </div>
              <FrVrfActionList
                icon="frIcon:question"
                options={MANUAL_LINK_LIST}
                style={{
                  marginRight: "70px",
                }}
                onSelectedOption={(event: CustomEvent) => {
                  window.open(event.detail.value.value);
                }}
              />
            </div>
            <div>
              <FrVrfSingleLineDisplayField
                label="User Name"
                style={{
                  "--vrf-single-line-display-field-font-size": "24px",
                  "--vrf-single-line-display-field-label-font-size": "12px",
                }}
                firstValue={pageState.edited.userName}
              />
            </div>
            <div>
              <FrVrfSingleLineDisplayField
                label="User ID"
                style={{
                  "--vrf-single-line-display-field-font-size": "16px",
                  "--vrf-single-line-display-field-label-font-size": "12px",
                }}
                firstValue={pageState.edited.userId}
              />
            </div>
            <div>
              <FrVrfSingleLineDisplayField
                label="Mail Address"
                style={{
                  "--vrf-single-line-display-field-font-size": "16px",
                  "--vrf-single-line-display-field-label-font-size": "12px",
                }}
                firstValue={pageState.edited.mlAdrs}
              />
            </div>
            <div>
              <FrVrfSingleLineDisplayField
                label="Assigned User Role"
                style={{
                  "--vrf-single-line-display-field-font-size": "16px",
                  "--vrf-single-line-display-field-label-font-size": "12px",
                }}
                firstValue={pageState.edited.userRoleList
                  ?.map((userRole) => {
                    return `${userRole.primFlg ? "*" : ""}${
                      userRole.roleName
                    }:${userRole.cvrgCd}`;
                  })
                  .join(", ")}
              />
            </div>
            <div>
              <FrVrfDropdownField
                labelContent="Role Code"
                selectedValue={pageState.edited.roleCd}
                required
                disabled={pageState.fromActionList}
                constraint="[IsNotBlank(value)]"
                onValueChanged={onChangeValueHandle("roleCd")}
                items={pageState.codes.roleCdList}
                errorMessage={pageState.fieldPath.roleCd}
              />
            </div>
            <div>
              <FrVrfDropdownField
                labelContent="Team Code"
                selectedValue={pageState.edited.teamCd}
                required
                disabled={
                  pageState.fromActionList ||
                  fixedTeamCds.has(pageState.edited.roleCd)
                }
                constraint="[IsNotBlank(value)]"
                onValueChanged={onChangeValueHandle("teamCd")}
                items={getFilteredTeamsCdList(pageState.edited.roleCd)}
                errorMessage={pageState.fieldPath.teamCd}
              />
            </div>
            <div>
              <FrVrfMultipleLineInputField
                labelContent="Note"
                content={pageState.edited.stkhldrNote}
                resize="both"
                style={{
                  "--vrf-multiple-line-input-field-input-height": "100px",
                  "--vrf-multiple-line-input-field-input-width": "600px",
                }}
                constraint="[Size(value, {max: 2000})]"
                onInputValueChanged={onChangeValueHandle("stkhldrNote")}
              />
            </div>
          </FrVrfFormPanel>
          <FrVrfFooter>
            <FrVrfButton
              content="Cancel"
              theme="secondary"
              size="large"
              onClicked={onClickCancel}
            />
            <FrVrfButton
              content="Update"
              theme="primary"
              size="large"
              disabled={
                JSON.stringify(pageState.saved) ===
                  JSON.stringify(pageState.edited) ||
                pageState.status.invalidList.length !== 0 ||
                pageState.status.notFound
              }
              onClicked={onClickUpdate}
            />
          </FrVrfFooter>
        </div>
      </>
    );
  };

  return createModal("Input Stakeholder", getContent(), onClickCancel, "large");
};
